import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import useModules from "../hooks/useModules";

/**
 * Legacy route handler. When the user hits a top-level URL like
 *   /payroll/periods/{id}   or   /staff/{id}
 * (no `/m/{code}` prefix), fetch the entity, look up its `module_code`, and
 * redirect to the module-scoped route so the page mounts inside its shell.
 *
 * Props:
 *   fetchPath(id) → string   path under /api to hit for the lookup
 *   moduleIdField           name of the module id field in the response
 *                           (default: "module_id")
 *   targetPath(code, id) → string   destination route
 */
export default function LegacyRedirect({ fetchPath, moduleIdField = "module_id", targetPath }) {
    const { id } = useParams();
    const { modules, byId: modulesById, loading: modulesLoading } = useModules();
    const [status, setStatus] = useState({ state: "loading", to: null, toast: null, toastKind: "info" });

    useEffect(() => {
        // Iter59.p0 — Race condition fix: `useModules` fetches /api/modules
        // asynchronously. On a fresh page load this effect used to fire BEFORE
        // modules landed, `modulesById` was `{}`, and the redirect fell through
        // to the "requires module context" home-toast. Wait for modules to
        // finish loading before resolving; the id we need is always the id we
        // just fetched.
        if (modulesLoading) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(fetchPath(id));
                if (!alive) return;
                const modId = data?.[moduleIdField];
                let code = modulesById?.[modId]?.code || data?.module_code;
                // Belt-and-braces: if the id came back but modules cache
                // doesn't know it (stale cache, newly-created module), do a
                // one-off direct fetch before giving up.
                if (!code && modId) {
                    try {
                        const { data: mods } = await api.get("/modules");
                        const found = (mods || []).find((m) => m.id === modId);
                        if (found?.code) code = found.code;
                    } catch { /* fall through to the notfound branch below */ }
                }
                if (!alive) return;
                if (!code) {
                    setStatus({
                        state: "notfound",
                        to: "/",
                        toast: "Redirected to Home — the requested item requires module context.",
                        toastKind: "info",
                    });
                    return;
                }
                setStatus({ state: "ready", to: targetPath(String(code).toLowerCase(), id), toast: null, toastKind: "info" });
            } catch {
                if (alive) setStatus({
                    state: "notfound",
                    to: "/",
                    toast: "This record no longer exists or you don't have access.",
                    toastKind: "error",
                });
            }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, modulesLoading, modules.length]);

    if (status.state === "ready" && status.to) {
        return <Navigate to={status.to} replace />;
    }
    if (status.state === "notfound") {
        return (
            <Navigate
                to={status.to || "/"}
                replace
                state={{ toast: status.toast, toastKind: status.toastKind }}
            />
        );
    }
    // Loading state — brief spinner while we resolve the module.
    return (
        <div className="min-h-[40vh] flex items-center justify-center" data-testid="legacy-redirect-loading">
            <div className="text-sm text-[color:var(--asl-muted)]">Loading…</div>
        </div>
    );
}
